import React, { useMemo } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import { theme } from '../../../../theme';

const ClientChatConversationList = ({
  conversations,
  query,
  onQueryChange,
  onSelect,
  onNewConversation,
}) => {
  const visible = useMemo(() => (
    [...conversations]
      .filter((item) => {
        const haystack = [
          item.salon?.name,
          item.subject,
          item.last_message?.content,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(query.trim().toLowerCase());
      })
      .sort((left, right) => (
        new Date(right.last_message_at || right.updated_at || 0)
        - new Date(left.last_message_at || left.updated_at || 0)
      ))
  ), [conversations, query]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={{ p: 2, pb: 1.25 }}>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Rechercher une discussion"
          inputProps={{ 'aria-label': 'Rechercher une discussion' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon
                  fontSize="small"
                  sx={{ color: theme.colors.primary[800] }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              backgroundColor: 'white',
              '&.Mui-focused fieldset': {
                borderColor: theme.colors.primary[700],
              },
            },
          }}
        />
        <Button
          fullWidth
          color="inherit"
          variant="contained"
          startIcon={<AddCommentOutlinedIcon />}
          onClick={onNewConversation}
          sx={{
            mt: 1.25,
            borderRadius: '12px',
            backgroundColor: theme.colors.primary[800],
            color: 'white',
            textTransform: 'none',
            fontWeight: 800,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: theme.colors.primary[900],
              boxShadow: 'none',
            },
          }}
        >
          Nouvelle discussion
        </Button>
      </Box>

      <List
        aria-label="Discussions"
        disablePadding
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1 }}
      >
        {visible.map((conversation) => {
          const unread = Number(conversation.unread_count || 0);
          const name = conversation.salon?.name || 'Salon';
          return (
            <ListItemButton
              key={conversation.id}
              onClick={() => onSelect(conversation)}
              sx={{
                mb: 0.75,
                borderRadius: '14px',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: theme.colors.primary[50],
                },
              }}
            >
              <ListItemAvatar>
                <Badge
                  badgeContent={unread}
                  color="error"
                  invisible={!unread}
                  overlap="circular"
                >
                  <Avatar
                    src={
                      conversation.salon?.profile_photo
                      || conversation.salon?.logo
                      || undefined
                    }
                    alt=""
                    sx={{
                      backgroundColor: theme.colors.secondary[300],
                      color: theme.colors.secondary[950],
                      fontWeight: 800,
                    }}
                  >
                    {name.charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={name}
                secondary={
                  conversation.last_message?.content
                  || conversation.subject
                  || 'Démarrer la discussion'
                }
                primaryTypographyProps={{
                  noWrap: true,
                  sx: { fontWeight: unread ? 800 : 700 },
                }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {!visible.length && (
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {query
              ? 'Aucune discussion trouvée.'
              : 'Vous n’avez pas encore de discussion.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ClientChatConversationList;
